import { useTranslate } from '@tolgee/react';
import { Alert, Divider, Dropdown, Menu, Tooltip, Tree } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import { MenuArrowDownSvg } from '../../../assets/icons';
import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import FolderSvg from '../../../assets/icons/FolderSvg';
import theme from '../../../assets/theme';
import useCopyDataBetweenTenants from '../../../hooks/useCopyDataBetweenTenants';
import useFolders from '../../../hooks/useFolders';
import useMoveToFolder from '../../../hooks/useMoveToFolder';
import useRouterMatch from '../../../hooks/useRouterMatch';
import useTenants from '../../../hooks/useTenants';
import useWorkSpace from '../../../hooks/useWorkSpace';
import useWorkspaceFolders from '../../../hooks/useWorkspaceFolders';
import { ById, Maybe } from '../../../services/backendService/types';
import { getExpandedTreeNodes } from '../../../utils/blueprintUtils';
import { getTitle } from '../../../utils/templateUtils';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../../_molecules/BaseModal';
import { Slide } from '../../SlideComponents/slide.types';

export type MoveToFolderData = {
  type: 'lesson' | 'folder';
  id: string;
  title?: string | null;
  parentId?: string | null;
  isPrivate: boolean;
  slides?: Slide[] | null;
  onMoveSuccess?: (newParentId: string | null, isPrivate: boolean) => void;
};

type UIFolder = {
  key: string;
  title: Maybe<string>;
  parentId: Maybe<string>;
  icon: JSX.Element;
  children: Array<UIFolder>;
};

const MoveToFolder = ({
  data,
  onDone,
  onCancel,
}: {
  data: MoveToFolderData;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const { moveLesson, isMoving, moveFolder } = useMoveToFolder();
  const {
    copyLessonBetweenTenants,
    isCopyingBetweenTenants,
    copyFolderBetweenTenants,
  } = useCopyDataBetweenTenants();
  const { allFolders: folders } = useFolders();
  const { data: tenantsData } = useTenants();
  const { tenantId } = useRouterMatch();
  const { allFolders: workspaceFolders } = useWorkspaceFolders();

  const [selectedNode, setSelectedNode] = useState<{
    key: string;
    isPrivate: boolean;
  } | null>({
    key: 'root',
    isPrivate: true,
  });
  const [selectedTenant, setSelectedTenant] = useState<
    { tenantId: string; tenantName: string } | undefined
  >(undefined);
  const { workspace } = useWorkSpace();
  const [expandedKeys, setExpandedKeys] = useState<string[]>();
  const [expandedWorkspaceKeys, setExpandedWorkspaceKeys] = useState<string[]>();
  const { t } = useTranslate();

  const handleSelectedNode = (s: Array<string | number>, isPrivate: boolean) => {
    const selected = s[0];
    setSelectedNode({
      key: selected ? selected.toString() : data.parentId ? data.parentId : 'root',
      isPrivate: selected ? isPrivate : data.isPrivate,
    });
  };
  useEffect(() => {
    if (data) {
      if (data && data.parentId) {
        setSelectedNode({ key: data.parentId, isPrivate: data.isPrivate });
      } else {
        setSelectedNode({ key: 'root', isPrivate: true });
      }
    }
  }, [data]);

  //private tree
  useEffect(() => {
    if (folders && data && data.isPrivate) {
      if (data.parentId) {
        setExpandedKeys(['root', ...getExpandedTreeNodes(folders, data.parentId)]);
      } else if (data && !data.parentId) {
        setExpandedKeys([
          'root',
          ...folders.filter(folder => !folder.parentId).map(folder => folder.id),
        ]);
      }
    }
  }, [folders, data]);

  // Workspace tree
  useEffect(() => {
    if (
      workspace &&
      workspaceFolders &&
      data &&
      data.isPrivate === false &&
      data.parentId
    ) {
      setExpandedWorkspaceKeys([
        data.parentId,
        ...getExpandedTreeNodes(workspaceFolders, data.parentId, workspace.id),
      ]);
    }
  }, [data, workspace, workspaceFolders]);

  // My lesson data tree
  const { dataTree, entityMap } = useMemo(() => {
    if (folders) {
      const icon = (
        <IconWrapper
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          height="16px"
          width="16px"
        >
          <FolderSvg />
        </IconWrapper>
      );

      const entityMap: ById<UIFolder> = {
        root: {
          key: 'root',
          title: t('My lessons', 'My lessons'),
          parentId: null,
          icon,
          children: [],
        },
      };
      folders.forEach(folder => {
        entityMap[folder.id] = {
          key: folder.id,
          title: folder.title,
          parentId: folder.parentId,
          icon,
          children: [],
        };
      });

      const dataTree = [entityMap['root']];
      folders.forEach(folder => {
        if (!!folder.parentId) {
          if (folder.parentId === data.id || folder.id === data.id) {
            // hide subfolders within the current selected folder/template
            return;
          }
          if (entityMap[folder.parentId]) {
            entityMap[folder.parentId].children?.push(entityMap[folder.id]);
          }
        } else {
          if (folder.id === data.id) {
            // hide subfolders within the current selected folder/template
            return;
          }
          dataTree[0].children.push(entityMap[folder.id]);
        }
      });
      return { dataTree, entityMap };
    }
    return { dataTree: undefined, entityMap: undefined };
  }, [folders, t, data.id]);

  // tenant tree
  const availableTenants = useMemo(() => {
    const allTenants = tenantsData?.user.availableTenants;
    if (allTenants && tenantId && allTenants.length > 1) {
      return allTenants;
    }
  }, [tenantsData, tenantId]);

  // Workspace expanded tree
  const { workspaceDataTree, workspaceEntityMap } = useMemo(() => {
    if (workspaceFolders && workspace) {
      const icon = (
        <IconWrapper
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          height="16px"
          width="16px"
        >
          <FolderSvg />
        </IconWrapper>
      );

      const workspaceEntityMap: ById<UIFolder> = {};
      (workspaceEntityMap[workspace.id] = {
        key: workspace.id,
        title: workspace.title,
        parentId: null,
        icon,
        children: [],
      }),
        workspaceFolders.forEach(folder => {
          workspaceEntityMap[folder.id] = {
            key: folder.id,
            title: folder.title,
            parentId: folder.parentId,
            icon,
            children: [],
          };
        });

      const workspaceDataTree = [workspaceEntityMap[workspace.id]];

      workspaceFolders.forEach(workspaceFolder => {
        if (workspaceFolder.parentId) {
          if (
            workspaceFolder.parentId === workspace.id &&
            workspaceDataTree?.[0] &&
            workspaceDataTree?.[0].children
          ) {
            // If the workspaceFolder's parentId is the same as the workspace.id then add it to the root
            workspaceDataTree?.[0].children.push(workspaceEntityMap[workspaceFolder.id]);
          } else {
            // If there is a parentId but it's not the workspace.id then add it to the correct parent folder
            if (
              workspaceEntityMap?.[workspaceFolder.parentId] &&
              workspaceEntityMap?.[workspaceFolder.id]
            ) {
              workspaceEntityMap[workspaceFolder.parentId].children.push(
                workspaceEntityMap[workspaceFolder.id],
              );
            }
          }
        }
      });
      return { workspaceDataTree, workspaceEntityMap };
    }
    return { workspaceDataTree: undefined, workspaceEntityMap: undefined };
  }, [workspaceFolders, workspace]);

  const handleMove = (
    selectedNode: {
      key: string;
      isPrivate: boolean;
    } | null,
    intent: 'switchingFolder' | 'switchingTenant' | '',
  ) => {
    if (intent === 'switchingFolder' && selectedNode) {
      const newParentId = selectedNode.key === 'root' ? null : selectedNode.key;
      if (data.type === 'lesson') {
        moveLesson({
          id: data.id,
          isPrivate: selectedNode.isPrivate,
          destinationFolderId: newParentId,
          onSuccessFunction: () => {
            data.onMoveSuccess?.(newParentId, selectedNode.isPrivate);
            onDone();
          },
        });
      } else {
        // todo
        moveFolder({
          id: data.id,
          isPrivate: selectedNode.isPrivate,
          destinationFolderId: selectedNode.key === 'root' ? null : selectedNode.key,
          onSuccessFunction: () => {
            onDone();
          },
        });
      }
      return;
    }
    if (intent === 'switchingTenant' && selectedTenant) {
      if (selectedTenant.tenantId !== tenantId) {
        if (data.type === 'lesson') {
          copyLessonBetweenTenants({
            lessonId: data.id,
            fromTenantId: tenantId,
            toTenantId: selectedTenant.tenantId,
            onSuccessFunction: () => {
              onDone();
            },
          });
        } else {
          copyFolderBetweenTenants({
            folderId: data.id,
            fromTenantId: tenantId,
            toTenantId: selectedTenant.tenantId,
            onSuccessFunction: () => {
              onDone();
            },
          });
        }
      }
    }
  };

  const handleExpand = (keys: string[], isPrivate: boolean) => {
    if (isPrivate) {
      setExpandedKeys(keys);
    } else {
      setExpandedWorkspaceKeys(keys);
    }
  };
  const isButtonDisabled: {
    isDisabled: boolean;
    reason: 'switchingFolder' | 'switchingTenant' | '';
  } = useMemo(() => {
    if (availableTenants && !selectedNode) {
      if (selectedTenant && selectedTenant?.tenantId === tenantId) {
        return { isDisabled: true, reason: '' };
      }
      return { isDisabled: false, reason: 'switchingTenant' };
    }
    if (
      !selectedNode ||
      selectedNode.key === data.parentId ||
      (selectedNode.key === 'root' && !data.parentId)
    ) {
      return { isDisabled: true, reason: '' };
    }
    return { isDisabled: false, reason: 'switchingFolder' };
  }, [availableTenants, data.parentId, selectedNode, selectedTenant, tenantId]);
  const dropDownTenant = useMemo(() => {
    if (!availableTenants) return null;
    if (availableTenants.length <= 1) return null;
    return (
      <Menu>
        {availableTenants
          .filter(t => t.tenantId !== tenantId)
          .map(availableTenant => {
            return (
              <Menu.Item
                key={availableTenant.tenantId}
                onClick={() => {
                  setSelectedTenant(availableTenant);
                  setSelectedNode(null);
                }}
                disabled={availableTenant.tenantId === tenantId}
              >
                {availableTenant.tenantName}
              </Menu.Item>
            );
          })}
      </Menu>
    );
  }, [availableTenants, tenantId]);
  if (!entityMap) return null;
  return (
    <BaseModal
      title={`${t('Move')} ${
        !!data.title
          ? `"${getTitle({
              title: data.title,
              slides: data.type === 'lesson' ? data.slides : [],
              maxLength: 75,
            })}"`
          : ''
      }`}
      open
      onCancel={onCancel}
    >
      {workspace && (
        <TreeContainer>
          <Tree
            onSelect={s => {
              handleSelectedNode(s, false);
            }}
            selectedKeys={selectedNode?.key ? [selectedNode?.key] : undefined}
            expandedKeys={expandedWorkspaceKeys}
            onExpand={s => handleExpand(s as string[], false)}
            showIcon
            switcherIcon={
              <IconWrapper style={{ transform: 'rotate(90deg)' }}>
                <ArrowRightSvg />
              </IconWrapper>
            }
            treeData={workspaceDataTree}
          />
        </TreeContainer>
      )}
      <TreeContainer>
        <Tree
          onSelect={s => {
            handleSelectedNode(s, true);
            setSelectedTenant(undefined);
          }}
          selectedKeys={selectedNode?.key ? [selectedNode?.key] : undefined}
          expandedKeys={expandedKeys}
          onExpand={s => handleExpand(s as string[], true)}
          showIcon
          switcherIcon={
            <IconWrapper style={{ transform: 'rotate(90deg)' }}>
              <ArrowRightSvg />
            </IconWrapper>
          }
          treeData={dataTree}
        />
      </TreeContainer>
      {selectedNode && data.isPrivate && !selectedNode?.isPrivate && workspaceEntityMap && (
        <Alert
          message={
            <h3 style={{ color: 'white', fontWeight: 'bold' }}>{t('Are you sure?')}</h3>
          }
          style={{
            backgroundColor: theme.colors.red,
            color: theme.colors.white,
            fontWeight: 'bold',
          }}
          description={t('movingToFolderWarning1', {
            defaultValue:
              'Moving this into the workspace means all workspace members can see it',
          })}
          type="error"
          showIcon={false}
          closable={false}
        />
      )}
      {selectedNode && !data.isPrivate && selectedNode?.isPrivate === true && entityMap && (
        <Alert
          message={<h3 style={{ color: 'white', fontWeight: 'bold' }}>Are you sure?</h3>}
          style={{
            backgroundColor: theme.colors.red,
            color: theme.colors.white,
            fontWeight: 'bold',
          }}
          description={t('moveingToFolderWarning2', {
            defaultValue: 'Making this private means only you will be able to see it',
          })}
          type="error"
          showIcon={false}
          closable={false}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          flexWrap: 'wrap',
          marginTop: '20px',
        }}
      >
        {dropDownTenant && (
          <TenantsContainer>
            <Divider style={{ margin: '5px 0' }} />
            <div style={{ width: '100%' }}>
              <Tooltip title="Selecting another school will make a copy of the lessons and folders you've selected. The content will be copied into your private lessons.">
                <h3 style={{ width: 'fit-content' }}>{t('Copy to another school?')}</h3>
              </Tooltip>
            </div>
            <Dropdown overlay={dropDownTenant} overlayStyle={{ zIndex: '9999' }}>
              <FakeInput>
                <span style={{ flex: 1 }}>
                  {selectedTenant?.tenantName ?? t('Select school')}
                </span>
                <IconWrapper height="12px" width="12px" style={{ marginLeft: 20 }}>
                  <MenuArrowDownSvg />
                </IconWrapper>
              </FakeInput>
            </Dropdown>
          </TenantsContainer>
        )}
        <Button
          disabled={isButtonDisabled.isDisabled}
          width="100%"
          type="primary"
          loading={isMoving || isCopyingBetweenTenants}
          onClick={() => {
            handleMove(selectedNode, isButtonDisabled.reason);
          }}
        >
          {selectedNode?.isPrivate && (
            <>
              {selectedNode &&
              (selectedNode.key === data.parentId ||
                (selectedNode.key === 'root' && !data.parentId))
                ? t('Move to')
                : t('moveToFolder', 'Move to "{title}"', {
                    title: selectedNode ? entityMap[selectedNode.key].title : '',
                  })}
            </>
          )}
          {!selectedNode && <>{`Copy to "${selectedTenant?.tenantName ?? ''}"`}</>}
          {selectedNode?.isPrivate == false && workspaceEntityMap && (
            <>
              {selectedNode && selectedNode.key === data.parentId
                ? t('Move to')
                : t('moveToFolder', {
                    defaultValue: 'Move to "{{title}}"',
                    title: workspaceEntityMap
                      ? workspaceEntityMap[selectedNode?.key].title
                      : '',
                  })}
            </>
          )}
        </Button>
      </div>
    </BaseModal>
  );
};

export default MoveToFolder;

const FakeInput = styled.div`
  width: fit-content;
  display: flex;
  align-items: center;
  line-height: 22px;
  font-size: 16px;
  outline: none;
  border-radius: 5px;
  padding: 10px 15px;
  transition: all 0.3s cubic-bezier(0.645, 0.045, 0.355, 1);
  background: white;
  box-shadow: ${theme.effects.floatingCreatorElementShadow};

  &:hover,
  &:focus {
    /* background: ${theme.colors.greyLight}; */
  }

  &:hover {
    box-shadow: ${theme.effects.floatingCreatorElementShadowHovered};
  }
`;

const TreeContainer = styled.div`
  width: 100%;
  padding: 10px 0px;
  .ant-tree {
    background: transparent;
  }
  .ant-tree-title {
    font-weight: bold;
  }
  &&&& .ant-tree-node-selected {
    color: white;
    background-color: ${theme.colors.green};
  }

  && .ant-tree-node-content-wrapper {
    border-radius: 5px;
  }
`;

const TenantsContainer = styled.div`
  width: 100%;
  padding-bottom: 10px;
  h3 {
    font-weight: bold;
  }
  display: flex;
  flex-wrap: wrap;
  gap: 5px;
  z-index: 99900 !important;
`;
